"use client"

import React, { useState, useEffect, useMemo } from "react"
import axios from "axios"
import { DATASETS } from "./data/datasets"
import { useTheme } from "./theme/ThemeProvider"
import GraphLogsModal from "./components/GraphLogsModal"
import GraphControlsPanel from "./components/GraphControlsPanel"
import GraphCanvas from "./components/GraphCanvas"
import RunBreadcrumb from "./components/RunBreadcrumb"
import { IterationsModal } from "./components/IterationsModal"
import { QUANTUM_DATASET_IDS } from "./data/quantumCsvRegistry"
import {
  getHardcodedEdges,
  getHardcodedNodeTypes,
  hasHardcodedEdges,
  hasHardcodedNodeTypes,
  mergeEdges,
  mergeNodeTypes,
} from "./data/hardcodedNodePatterns"

// Demo runs (PG-HIVE only)
import { PG_SCHEMA_RUNS } from "./data/pg_runs_all"

const datasetById = Object.fromEntries(DATASETS.map((d) => [d.id, d]))
const QUANTUM_DATASET_ID_SET = new Set(QUANTUM_DATASET_IDS)

// -------- ιστορικό runs στο localStorage --------
const runsKey = (dataset) => `pg_runs_${dataset}`

function loadRuns(dataset) {
  try {
    return JSON.parse(localStorage.getItem(runsKey(dataset)) || "[]")
  } catch (e) {
    return []
  }
}

function saveRuns(dataset, runs) {
  try {
    localStorage.setItem(runsKey(dataset), JSON.stringify(runs))
  } catch (e) {}
}

function addRun(dataset, runEntry, limit = 50) {
  const runs = loadRuns(dataset)
  runs.unshift(runEntry)
  if (runs.length > limit) runs.length = limit
  saveRuns(dataset, runs)
}

const makeId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

function makeColorPicker(isDarkTheme) {
  const light = [
    "#ef4444",
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#8b5cf6",
    "#ec4899",
    "#14b8a6",
    "#22d3ee",
    "#f97316",
    "#84cc16",
    "#f87171",
    "#60a5fa",
    "#34d399",
    "#fbbf24",
    "#a78bfa",
    "#f472b6",
    "#2dd4bf",
    "#5eead4",
    "#fd7e14",
    "#bef264",
    "#dc2626",
    "#2563eb",
    "#059669",
    "#d97706",
    "#7c3aed",
  ]
  const dark = [
    "#fca5a5",
    "#93c5fd",
    "#6ee7b7",
    "#fde68a",
    "#c4b5fd",
    "#f9a8d4",
    "#5eead4",
    "#a5f3fc",
    "#fdba74",
    "#bef264",
    "#f87171",
    "#60a5fa",
    "#34d399",
    "#fbbf24",
    "#a78bfa",
    "#f472b6",
    "#2dd4bf",
    "#5eead4",
    "#fd7e14",
    "#bef264",
    "#fca5a5",
    "#93c5fd",
    "#6ee7b7",
    "#fde68a",
    "#c4b5fd",
  ]
  const palette = isDarkTheme ? dark : light
  const assigned = new Map()
  let i = 0
  const fallback = () => {
    const r = Math.floor(160 + Math.random() * 80)
    const g = Math.floor(160 + Math.random() * 80)
    const b = Math.floor(160 + Math.random() * 80)
    return `rgb(${r},${g},${b})`
  }
  return (node) => {
    const key = node.group || node.id || "default"
    if (!assigned.has(key)) {
      assigned.set(key, i < palette.length ? palette[i++] : fallback())
    }
    return assigned.get(key)
  }
}

function extractLogs(runOrText) {
  const run = typeof runOrText === "object" ? runOrText : null
  if (!run) return String(runOrText || "") || "No logs available for this run."
  if (typeof run.logs === "string") return run.logs
  if (Array.isArray(run.logs)) return run.logs.join("\n")
  if (typeof run.log === "string") return run.log
  if (run.stdout || run.stderr) {
    const parts = []
    if (run.stdout) parts.push(`--- stdout ---\n${String(run.stdout)}`)
    if (run.stderr) parts.push(`--- stderr ---\n${String(run.stderr)}`)
    return parts.join("\n\n")
  }
  if (run.events) return `--- events ---\n${JSON.stringify(run.events, null, 2)}`
  if (run.debug)
    return `--- debug ---\n${JSON.stringify(run.debug, null, 2)}`
  return "No logs available for this run."
}

const EMPTY_METRICS = {
  nodes: { f1Score: 0, precision: 0, recall: 0 },
  edges: { f1Score: 0, precision: 0, recall: 0 },
}

export default function Graph_Info() {
  const { theme } = useTheme()
  const isDark = theme === "dark"

  const graphWidth =
    typeof window !== "undefined" ? window.innerWidth * 0.6 - 32 : 800
  const graphHeight =
    typeof window !== "undefined" ? window.innerHeight * 0.75 : 600

  const [nodeTypes, setNodeTypes] = useState([])
  const [edges, setEdges] = useState([])
  const [metrics, setMetrics] = useState(EMPTY_METRICS)

  const [runParams, setRunParams] = useState({
    incremental: false,
    incrementalValue: "100000",
    knownTypeLabelPercentage: "100",
    dataset: "POLE",
    lshVariant: "ELSH",
    adaptive: true,
    spectralAutoBalancing: true,
    quantumK: 20,
    lshHashTables: 20,
    lshHashBucketLength: 4,
    jaccardSimilarityThreshold: 0.75,
  })

  const [nodeTypesDialogOpen, setNodeTypesDialogOpen] = useState(false)
  const [edgesDialogOpen, setEdgesDialogOpen] = useState(false)
  const [runs, setRuns] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const [logsOpen, setLogsOpen] = useState(false)
  const [logsQuery, setLogsQuery] = useState("")
  const [currentLogs, setCurrentLogs] = useState("")

  const [itersOpen, setItersOpen] = useState(false)
  const [itersRun, setItersRun] = useState(null)

  const [selectedRunId, setSelectedRunId] = useState(null)
  const [, setViewingSnapshot] = useState(false)

  const activeRun =
    (selectedRunId
      ? runs.find((r) => r.id === selectedRunId)
      : runs[0]) || runs[0]

  const { isValid, errors } = useMemo(() => {
    const errs = []
    const isLshVariant =
      runParams.lshVariant === "ELSH" || runParams.lshVariant === "MinHash"
    if (runParams.incremental) {
      const inc = Number(runParams.incrementalValue)
      if (!(inc > 0)) errs.push("Incremental value must be > 0.")
    }
    if (isLshVariant && !runParams.adaptive) {
      if (!(Number(runParams.lshHashTables) > 0))
        errs.push("LSH Hash Tables (T) must be > 0.")
      if (!(Number(runParams.lshHashBucketLength) > 0))
        errs.push("LSH Hash Bucket Length (b) must be > 0.")
      const j = Number(runParams.jaccardSimilarityThreshold)
      if (!(j > 0)) errs.push("Jaccard Similarity must be > 0.")
      if (j > 1) errs.push("Jaccard Similarity cannot be > 1.")
    }
    return { isValid: errs.length === 0, errors: errs }
  }, [runParams])

  const colorFor = useMemo(() => makeColorPicker(isDark), [isDark])

  const applyHardcodedSchema = (datasetId, baseNodes = [], baseEdges = []) => {
    let nextNodes = Array.isArray(baseNodes) ? [...baseNodes] : []
    let nextEdges = Array.isArray(baseEdges) ? [...baseEdges] : []

    const hardcodedNodeTypes = getHardcodedNodeTypes(datasetId)
    const useHardcodedNodes = hasHardcodedNodeTypes(datasetId)
    if (hardcodedNodeTypes.length > 0) {
      nextNodes = useHardcodedNodes
        ? [...hardcodedNodeTypes]
        : mergeNodeTypes(nextNodes, hardcodedNodeTypes)
    }

    const hardcodedEdges = getHardcodedEdges(datasetId)
    const useHardcodedEdgeTypes = hasHardcodedEdges(datasetId)
    if (hardcodedEdges.length > 0) {
      nextEdges = useHardcodedEdgeTypes
        ? [...hardcodedEdges]
        : mergeEdges(nextEdges, hardcodedEdges)
      const existingLabels = new Set(nextNodes.map((n) => n.name))
      hardcodedEdges.forEach((edge) => {
        if (!existingLabels.has(edge.source)) {
          nextNodes.push({
            name: edge.source,
            looseDetails: [],
            strictDetails: [],
          })
          existingLabels.add(edge.source)
        }
        if (!existingLabels.has(edge.target)) {
          nextNodes.push({
            name: edge.target,
            looseDetails: [],
            strictDetails: [],
          })
          existingLabels.add(edge.target)
        }
      })
    }

    return { nodes: nextNodes, edges: nextEdges }
  }

  const decorateRunForDataset = (run, datasetId) => {
    if (!run || !run.snapshot) return run
    const normalizedDataset =
      datasetId || run._dataset || run.params?.dataset || runParams.dataset
    const patched = applyHardcodedSchema(
      normalizedDataset,
      run.snapshot.nodes || [],
      run.snapshot.edges || []
    )

    return {
      ...run,
      snapshot: { nodes: patched.nodes, edges: patched.edges },
      summary: {
        ...(run.summary || {}),
        nodeTypes: patched.nodes.length,
        edges: patched.edges.length,
      },
    }
  }

  const loadRunSnapshot = (run, datasetOverride = null) => {
    if (!run || !run.snapshot) return
    const datasetId =
      datasetOverride || run._dataset || run.params?.dataset || runParams.dataset
    const patched = applyHardcodedSchema(
      datasetId,
      run.snapshot.nodes || [],
      run.snapshot.edges || []
    )
    setNodeTypes(patched.nodes || [])
    setEdges(patched.edges || [])
    setMetrics(run.metrics || EMPTY_METRICS)
  }

  const isQuantumVariant = (variant) =>
    variant === "QUANTUM_FAST" || variant === "QUANTUM"

  const getLatestDemoSchemaRun = (datasetId) => {
    const demoRuns = PG_SCHEMA_RUNS[datasetId] || []
    if (!demoRuns.length) return null

    return demoRuns.reduce((latest, run) => {
      if (!run?.snapshot) return latest
      if (!latest?.snapshot) return run
      return (run.ts || 0) >= (latest.ts || 0) ? run : latest
    }, null)
  }

  const buildDemoQuantumRun = (effectiveRunParams) => {
    const datasetId = effectiveRunParams.dataset
    const sourceRun = getLatestDemoSchemaRun(datasetId)
    const sourceSnapshot = sourceRun?.snapshot || {}
    const patchedSnapshot = applyHardcodedSchema(
      datasetId,
      sourceSnapshot.nodes || [],
      sourceSnapshot.edges || []
    )

    return {
      id: makeId(),
      ts: Date.now(),
      params: { ...effectiveRunParams },
      metrics: sourceRun?.metrics || EMPTY_METRICS,
      summary: {
        nodeTypes: patchedSnapshot.nodes.length,
        edges: patchedSnapshot.edges.length,
      },
      snapshot: {
        nodes: patchedSnapshot.nodes,
        edges: patchedSnapshot.edges,
      },
      iterations: Array.isArray(sourceRun?.iterations)
        ? sourceRun.iterations.map((iteration) => {
            const iterPatched = applyHardcodedSchema(
              datasetId,
              iteration?.snapshot?.nodes || [],
              iteration?.snapshot?.edges || []
            )
            return {
              ...iteration,
              snapshot: {
                nodes: iterPatched.nodes,
                edges: iterPatched.edges,
              },
              summary: {
                ...(iteration?.summary || {}),
                nodeTypes: iterPatched.nodes.length,
                edges: iterPatched.edges.length,
              },
            }
          })
        : [],
      logs: [
        `[demo] Start ${effectiveRunParams.lshVariant} on ${datasetId}`,
        "[demo] Reusing PG-HIVE schema snapshot for visualization",
        "[demo] Node and edge types mirrored from the PG-HIVE demo JSON",
        "[demo] Done",
      ],
      _dataset: datasetId,
    }
  }

  const matchesRunConfig = (run, params) => {
    const runParams = run?.params || {}
    const keys = [
      "dataset",
      "lshVariant",
      "incremental",
      "incrementalValue",
      "adaptive",
      "spectralAutoBalancing",
      "knownTypeLabelPercentage",
      "knownNodesPercentage",
      "quantumK",
      "quantumFixedK",
      "quantumTopK",
      "quantumMaxGroupSize",
      "quantumMinClusterSize",
      "quantumMinSimilarity",
      "quantumBalanceTarget",
      "quantumBalanceLambda",
      "quantumPowerIters",
      "quantumTolerance",
      "quantumSaIters",
      "quantumSaRestarts",
      "quantumRandomSeed",
    ]

    return keys.every((key) => runParams[key] === params?.[key])
  }

  const maybeSeedDemoRun = (datasetId, params, existingRuns = []) => {
    if (
      !params ||
      !isQuantumVariant(params.lshVariant) ||
      !QUANTUM_DATASET_ID_SET.has(datasetId)
    ) {
      return existingRuns
    }

    const nextParams = { ...params, dataset: datasetId }
    const latestRun = Array.isArray(existingRuns) ? existingRuns[0] : null
    if (latestRun && matchesRunConfig(latestRun, nextParams)) {
      return existingRuns
    }

    addRun(datasetId, buildDemoQuantumRun(nextParams))
    return loadRuns(datasetId)
  }

  // φιλτράρουμε baselines αν είμαστε σε demo mode
  const filterRunsForMode = (runsArr, demoMode) => {
    if (!Array.isArray(runsArr)) return []
    if (!demoMode) return runsArr
    return runsArr.filter((r) => !r.kind || r.kind !== "baseline")
  }

  useEffect(() => {
    if (typeof window === "undefined") return

    let initialDataset = "POLE"
    let mergedParams = null
    let selectedFromDashboard = null

    const demoMode =
      window.sessionStorage.getItem("pg_demo_mode") === "true"

    try {
      const rawParams = window.sessionStorage.getItem("pg_last_run_params")
      if (rawParams) {
        const parsed = JSON.parse(rawParams)
        initialDataset = parsed.dataset || parsed._dataset || initialDataset
        mergedParams = parsed
      }

      const rawSelected = window.sessionStorage.getItem("pg_selected_run")
      if (rawSelected) {
        const parsedSel = JSON.parse(rawSelected)
        if (parsedSel.dataset) {
          initialDataset = parsedSel.dataset
        }
        if (parsedSel.id) {
          selectedFromDashboard = parsedSel.id
        }
      }
    } catch (e) {
      console.warn("Failed to restore session selection", e)
    }

    try {
      Object.keys(window.localStorage)
        .filter((key) => key.startsWith("pg_cache_"))
        .forEach((key) => window.localStorage.removeItem(key))
    } catch (e) {}

    setRunParams((prev) => ({
      ...prev,
      ...(mergedParams || {}),
      dataset: initialDataset,
    }))

    // 1) runs από localStorage
    let storedRuns = loadRuns(initialDataset)

    if (demoMode && !selectedFromDashboard) {
      storedRuns = maybeSeedDemoRun(
        initialDataset,
        mergedParams || { dataset: initialDataset },
        storedRuns
      )
    }

    // 2) αν δεν υπάρχουν και είμαστε σε demo mode, βάλε τα PG_SCHEMA_RUNS
    if ((!storedRuns || storedRuns.length === 0) && demoMode) {
      const demoRuns = PG_SCHEMA_RUNS[initialDataset] || []
      if (demoRuns.length > 0) {
        storedRuns = [...demoRuns]
        saveRuns(initialDataset, storedRuns)
      }
    }

    const visibleRuns = filterRunsForMode(storedRuns || [], demoMode).map((r) =>
      decorateRunForDataset(r, initialDataset)
    )
    setRuns(visibleRuns)

    // nodes / edges / metrics
    const savedNodes = window.localStorage.getItem("pg_nodes")
    const savedEdges = window.localStorage.getItem("pg_edges")
    const savedMetricsRaw = window.localStorage.getItem("pg_metrics")

    if (savedNodes && savedEdges && savedMetricsRaw) {
      const patched = applyHardcodedSchema(
        initialDataset,
        JSON.parse(savedNodes),
        JSON.parse(savedEdges)
      )
      setNodeTypes(patched.nodes)
      setEdges(patched.edges)
      setMetrics(JSON.parse(savedMetricsRaw))
    } else {
      const latest = visibleRuns[0]
      if (latest?.snapshot) {
        const patched = applyHardcodedSchema(
          initialDataset,
          latest.snapshot.nodes || [],
          latest.snapshot.edges || []
        )
        setNodeTypes(patched.nodes)
        setEdges(patched.edges)
        setMetrics(latest.metrics || EMPTY_METRICS)
      } else {
        const patched = applyHardcodedSchema(initialDataset, [], [])
        setNodeTypes(patched.nodes)
        setEdges(patched.edges)
        setMetrics(EMPTY_METRICS)
      }
    }

    // active run + logs
    if (selectedFromDashboard) {
      const found = visibleRuns.find((x) => x.id === selectedFromDashboard)
      if (found) {
        setSelectedRunId(found.id)
        loadRunSnapshot(found, initialDataset)
        setViewingSnapshot(true)
        setCurrentLogs(extractLogs(found))
      } else {
        setSelectedRunId(visibleRuns[0]?.id ?? null)
        setCurrentLogs(
          visibleRuns.length
            ? extractLogs(visibleRuns[0])
            : "No logs available for this run."
        )
      }
    } else {
      setSelectedRunId(visibleRuns[0]?.id ?? null)
      setCurrentLogs(
        visibleRuns.length
          ? extractLogs(visibleRuns[0])
          : "No logs available for this run."
      )
    }

    window.localStorage.removeItem("pg_nodes")
    window.localStorage.removeItem("pg_edges")
    window.localStorage.removeItem("pg_metrics")
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDatasetChange = (value) => {
    setRunParams((prev) => ({ ...prev, dataset: value }))
    setSelectedRunId(null)
    setViewingSnapshot(false)

    const demoMode =
      typeof window !== "undefined" &&
      window.sessionStorage.getItem("pg_demo_mode") === "true"

    const rawRuns = loadRuns(value)
    const filteredRuns = filterRunsForMode(rawRuns || [], demoMode).map((r) =>
      decorateRunForDataset(r, value)
    )
    setRuns(filteredRuns)

    const latest = filteredRuns[0]
    if (latest?.snapshot) {
      const patched = applyHardcodedSchema(
        value,
        latest.snapshot.nodes || [],
        latest.snapshot.edges || []
      )
      setNodeTypes(patched.nodes)
      setEdges(patched.edges)
      setMetrics(latest.metrics || EMPTY_METRICS)
    } else {
      const patched = applyHardcodedSchema(value, [], [])
      setNodeTypes(patched.nodes)
      setEdges(patched.edges)
      setMetrics(EMPTY_METRICS)
    }

    setCurrentLogs(
      filteredRuns.length
        ? extractLogs(filteredRuns[0])
        : "No logs available for this run."
    )
  }

  const fetchBackendLogs = async () => {
    try {
      const res = await axios.get("http://localhost:8080/logs")
      const data = res && res.data
      if (typeof data === "string") return data
      if (Array.isArray(data)) return data.join("\n")
      if (data && Array.isArray(data.lines))
        return data.lines.join("\n")
      if (data && typeof data.log === "string") return data.log
      return "No logs available for this run."
    } catch (e) {
      return "No logs available for this run."
    }
  }

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

  const handleRunClick = async (paramsOverride = null) => {
    if (!isValid) return
    setIsLoading(true)
    let logsText = ""
    try {
      const effectiveRunParams = paramsOverride || runParams
      const { dataset, incremental, incrementalValue } = effectiveRunParams
      const demoMode =
        typeof window !== "undefined" &&
        window.sessionStorage.getItem("pg_demo_mode") === "true"

      if (
        demoMode &&
        isQuantumVariant(effectiveRunParams.lshVariant) &&
        QUANTUM_DATASET_ID_SET.has(dataset)
      ) {
        const runEntry = buildDemoQuantumRun(effectiveRunParams)

        setCurrentLogs(extractLogs(runEntry))
        setMetrics(runEntry.metrics || EMPTY_METRICS)
        setNodeTypes(runEntry.snapshot?.nodes || [])
        setEdges(runEntry.snapshot?.edges || [])

        addRun(dataset, runEntry)
        const updatedRuns = (loadRuns(dataset) || []).map((r) =>
          decorateRunForDataset(r, dataset)
        )
        setRuns(updatedRuns)
        setSelectedRunId(runEntry.id)
        setViewingSnapshot(false)
        return
      }

      let body = dataset
      if (incremental) body += ` INCREMENTAL ${incrementalValue}`

      await axios.post("http://localhost:8080/start", body, {
        headers: { "Content-Type": "text/plain" },
      })

      let clusteringCompleted = false
      while (!clusteringCompleted) {
        try {
          const res = await axios.post(
            "http://localhost:8080/clustering-finished",
            { status: "check" }
          )
          if (res.status === 200) clusteringCompleted = true
        } catch (e) {}
        if (!clusteringCompleted) await sleep(10000)
      }

      logsText = await fetchBackendLogs()
      setCurrentLogs(logsText)

      // metrics
      let currentMetrics = metrics
      const metricsRes = await axios.get("http://localhost:8080/metrics")
      const metricsList = metricsRes.data
      if (Array.isArray(metricsList) && metricsList.length > 0) {
        const lastMetric = metricsList[metricsList.length - 1]
        const parsed = JSON.parse(lastMetric)
        currentMetrics = parsed
        setMetrics(parsed)
      }

      // node types
      let currentNodeTypes = []
      const nodeInfoRes = await axios.get("http://localhost:8080/node-info")
      const nodeInfoList = nodeInfoRes.data
      if (Array.isArray(nodeInfoList) && nodeInfoList.length > 0) {
        const lastNodeInfo = nodeInfoList[nodeInfoList.length - 1]
        const parsedNodeInfo = JSON.parse(lastNodeInfo)
        const nodeTypeMap = new Map()
        parsedNodeInfo.clusters.forEach((entry) => {
          entry.labels.forEach((label) => {
            const existing =
              nodeTypeMap.get(label) || {
                name: label,
                strictDetails: [],
                looseDetails: [],
              }
            const optionalDetails = (entry.optionalProperties || []).map(
              (prop) => ({
                name: prop.replace(/:.*$/, ""),
                type: prop.split(":")[1] || "string",
              })
            )
            const looseDetails = (entry.properties || []).map((prop) => ({
              name: prop.replace(/:.*$/, ""),
              type: prop.split(":")[1] || "string",
            }))
            nodeTypeMap.set(label, {
              ...existing,
              strictDetails: [...existing.strictDetails, ...optionalDetails],
              looseDetails: [...existing.looseDetails, ...looseDetails],
            })
          })
        })
        currentNodeTypes = Array.from(nodeTypeMap.values())
      }
      const withHardcodedNodes = applyHardcodedSchema(dataset, currentNodeTypes, [])
      currentNodeTypes = withHardcodedNodes.nodes

      // edges
      let currentEdges = []
      const edgeInfoRes = await axios.get("http://localhost:8080/edge-info")
      const edgeInfoList = edgeInfoRes.data
      if (Array.isArray(edgeInfoList) && edgeInfoList.length > 0) {
        const lastEdgeInfo = edgeInfoList[edgeInfoList.length - 1]
        const parsedEdgeInfo = JSON.parse(lastEdgeInfo)

        const updatedEdges = []
        const existingLabels = new Set(
          currentNodeTypes.map((t) => t.name)
        )
        const newNodeTypes = [...currentNodeTypes]

        parsedEdgeInfo.edges.forEach((entry) => {
          entry.srcLabels.forEach((src) => {
            entry.dstLabels.forEach((dst) => {
              updatedEdges.push({
                source: src,
                target: dst,
                relationship: entry.relationship,
                looseDetails: (entry.properties || []).map((prop) => ({
                  name: prop.replace(/:.*$/, ""),
                  type: prop.split(":")[1] || "string",
                })),
                strictDetails: (entry.optionalProperties || []).map(
                  (prop) => ({
                    name: prop.replace(/:.*$/, ""),
                    type: prop.split(":")[1] || "string",
                  })
                ),
              })
              if (!existingLabels.has(src)) {
                newNodeTypes.push({
                  name: src,
                  looseDetails: [],
                  strictDetails: [],
                })
                existingLabels.add(src)
              }
              if (!existingLabels.has(dst)) {
                newNodeTypes.push({
                  name: dst,
                  looseDetails: [],
                  strictDetails: [],
                })
                existingLabels.add(dst)
              }
            })
          })
        })

        currentNodeTypes = newNodeTypes
        currentEdges = updatedEdges
      }
      const withHardcoded = applyHardcodedSchema(dataset, currentNodeTypes, currentEdges)
      currentNodeTypes = withHardcoded.nodes
      currentEdges = withHardcoded.edges

      setNodeTypes(currentNodeTypes)
      setEdges(currentEdges)

      const runEntry = {
        id: makeId(),
        ts: Date.now(),
        params: { ...effectiveRunParams },
        metrics: currentMetrics,
        summary: {
          nodeTypes: currentNodeTypes.length,
          edges: currentEdges.length,
        },
        snapshot: { nodes: currentNodeTypes, edges: currentEdges },
        logs: currentLogs || logsText || "No logs available for this run.",
      }
      addRun(dataset, runEntry)
      const updatedRuns = (loadRuns(dataset) || []).map((r) =>
        decorateRunForDataset(r, dataset)
      )
      setRuns(updatedRuns)

      setSelectedRunId(runEntry.id)
      setViewingSnapshot(false)
    } catch (error) {
      console.error("Error during clustering or polling:", error)
      const logsTextLocal = await fetchBackendLogs()
      setCurrentLogs(logsTextLocal)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelectRun = (runId) => {
    setSelectedRunId(runId)
    const r = runs.find((x) => x.id === runId) || runs[0]
    if (r) {
      loadRunSnapshot(r, r._dataset || r.params?.dataset || runParams.dataset)
      setViewingSnapshot(true)
    }
  }

  const handleBackToLatest = () => {
    const latest = runs[0]
    if (latest?.snapshot) {
      const patched = applyHardcodedSchema(
        runParams.dataset,
        latest.snapshot.nodes || [],
        latest.snapshot.edges || []
      )
      setNodeTypes(patched.nodes)
      setEdges(patched.edges)
      setMetrics(latest.metrics || EMPTY_METRICS)
    } else {
      const patched = applyHardcodedSchema(runParams.dataset, [], [])
      setNodeTypes(patched.nodes)
      setEdges(patched.edges)
      setMetrics(EMPTY_METRICS)
    }
    setViewingSnapshot(false)
    setSelectedRunId(null)
  }

  const latestRun = runs && runs.length > 0 ? runs[0] : null
  const datasetLabel =
    (datasetById[runParams.dataset] &&
      datasetById[runParams.dataset].label) ||
    runParams.dataset

  return (
    <div>
      <div>
        <RunBreadcrumb
          datasetLabel={datasetLabel}
          runs={runs}
          selectedRunId={selectedRunId || (runs[0] && runs[0].id)}
          onSelectRun={handleSelectRun}
          onBackToLatest={handleBackToLatest}
        />
      </div>
      <div className="flex h-50 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
        <GraphControlsPanel
          datasetId={runParams.dataset}
          datasetLabel={datasetLabel}
          runParams={runParams}
          setRunParams={setRunParams}
          nodeTypes={nodeTypes}
          edges={edges}
          metrics={metrics}
          isValid={isValid && !isLoading}
          lastRunTs={activeRun && activeRun.ts}
          errors={errors}
          colorFor={colorFor}
          onDatasetChange={handleDatasetChange}
          onRunClick={handleRunClick}
          onShowLogs={() => {
            setLogsQuery("")
            setLogsOpen(true)
          }}
          dialogs={{
            nodeTypesOpen: nodeTypesDialogOpen,
            setNodeTypesOpen: setNodeTypesDialogOpen,
            edgesOpen: edgesDialogOpen,
            setEdgesOpen: setEdgesDialogOpen,
          }}
          activeRun={activeRun}
          onShowIterations={() => {
            if (activeRun && activeRun.iterations && activeRun.iterations.length) {
              setItersRun(activeRun)
              setItersOpen(true)
            }
          }}
        />

        <GraphCanvas
          nodeTypes={nodeTypes}
          edges={edges}
          width={graphWidth}
          height={graphHeight}
          colorFor={colorFor}
          isDark={isDark}
        />

        <GraphLogsModal
          open={logsOpen}
          onClose={() => setLogsOpen(false)}
          datasetLabel={datasetLabel}
          latestRun={latestRun}
          text={currentLogs || extractLogs(latestRun || "")}
          query={logsQuery}
          onQueryChange={setLogsQuery}
        />

        <IterationsModal
          open={itersOpen}
          run={itersRun}
          onClose={() => {
            setItersOpen(false)
            setItersRun(null)
          }}
          datasetById={datasetById}
        />
      </div>
    </div>
  )
}
